import React, { useState } from 'react';
import { Sun, Moon, MonitorCog } from 'lucide-react';

const INK    = '#2B2620';
const WALNUT = '#8B4513';
const MOSS   = '#3B6B44';
const OCHRE  = '#A6752A';
const RUST   = '#A13D2D';
const STONE  = '#8C8275';
const PAPER  = '#FDFBF8';
const FONT_SANS = "'IBM Plex Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

const THEME_OPTIONS = [
  { id: 'light', Icon: Sun },
  { id: 'dark',  Icon: Moon },
  { id: 'auto',  Icon: MonitorCog },
];

const BRAND_SWATCHES = [
  { color: INK,    label: 'Ink' },
  { color: WALNUT, label: 'Walnut' },
  { color: MOSS,   label: 'Moss' },
  { color: OCHRE,  label: 'Ochre' },
  { color: RUST,   label: 'Rust' },
  { color: STONE,  label: 'Stone' },
];

function Toggle({ checked, onChange }) {
  return (
    <label style={{ position: 'relative', display: 'inline-block', width: '50px', height: '28px' }}>
      <input type="checkbox" checked={checked} onChange={onChange} style={{ display: 'none' }} />
      <span style={{
        position: 'absolute', cursor: 'pointer', top: 0, left: 0, right: 0, bottom: 0,
        background: checked ? WALNUT : '#ccc', borderRadius: '28px', transition: '0.3s'
      }}>
        <span style={{
          position: 'absolute', content: '""', height: '20px', width: '20px',
          left: checked ? '26px' : '4px', bottom: '4px', background: 'white', borderRadius: '50%', transition: '0.3s'
        }} />
      </span>
    </label>
  );
}

export default function Appearance() {
  const [theme, setTheme] = useState('auto');
  const [compactMode, setCompactMode] = useState(false);
  const [showWeekends, setShowWeekends] = useState(true);

  return (
    <div style={{ fontFamily: FONT_SANS }}>
      <h3 style={{
        fontSize: '15px',
        fontWeight: '600',
        color: INK,
        marginBottom: '16px'
      }}>
        Appearance
      </h3>

      <div style={{
        padding: '20px',
        background: PAPER,
        borderRadius: '12px',
        border: '1px solid rgba(139,69,19,0.1)'
      }}>
        {/* Theme Selection */}
        <div style={{ marginBottom: '24px' }}>
          <label style={{
            display: 'block',
            fontSize: '13px',
            fontWeight: '600',
            color: INK,
            marginBottom: '12px'
          }}>
            Theme
          </label>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '12px'
          }}>
            {THEME_OPTIONS.map(({ id, Icon }) => (
              <button
                key={id}
                onClick={() => setTheme(id)}
                style={{
                  padding: '16px',
                  background: theme === id ? `${WALNUT}14` : 'rgba(139,69,19,0.04)',
                  border: `2px solid ${theme === id ? WALNUT : 'transparent'}`,
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  textAlign: 'center'
                }}
              >
                <Icon size={22} strokeWidth={2} color={theme === id ? WALNUT : STONE} style={{ marginBottom: '8px' }} />
                <div style={{
                  fontSize: '13px',
                  fontWeight: '600',
                  color: theme === id ? WALNUT : STONE,
                  textTransform: 'capitalize'
                }}>
                  {id}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Compact Mode */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '16px 0',
          borderTop: '1px solid rgba(139,69,19,0.06)',
          borderBottom: '1px solid rgba(139,69,19,0.06)'
        }}>
          <div>
            <div style={{
              fontSize: '14px',
              fontWeight: '600',
              color: INK,
              marginBottom: '4px'
            }}>
              Compact Mode
            </div>
            <div style={{
              fontSize: '12px',
              color: STONE
            }}>
              Reduce spacing for more content visibility
            </div>
          </div>
          <Toggle checked={compactMode} onChange={(e) => setCompactMode(e.target.checked)} />
        </div>

        {/* Show Weekends */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '16px 0',
          borderBottom: '1px solid rgba(139,69,19,0.06)'
        }}>
          <div>
            <div style={{
              fontSize: '14px',
              fontWeight: '600',
              color: INK,
              marginBottom: '4px'
            }}>
              Show Weekends in Calendar
            </div>
            <div style={{
              fontSize: '12px',
              color: STONE
            }}>
              Display Saturday and Sunday columns
            </div>
          </div>
          <Toggle checked={showWeekends} onChange={(e) => setShowWeekends(e.target.checked)} />
        </div>

        {/* Color Scheme Preview */}
        <div style={{ marginTop: '24px' }}>
          <div style={{
            fontSize: '13px',
            fontWeight: '600',
            color: INK,
            marginBottom: '12px'
          }}>
            Brand Colors
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))',
            gap: '12px'
          }}>
            {BRAND_SWATCHES.map(s => (
              <ColorSwatch key={s.label} color={s.color} label={s.label} />
            ))}
          </div>
        </div>

        {/* Save Button */}
        <button
          style={{
            marginTop: '20px',
            width: '100%',
            padding: '12px',
            background: WALNUT,
            border: 'none',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: '600',
            fontFamily: FONT_SANS,
            color: 'white',
            cursor: 'pointer',
            transition: 'background 0.2s'
          }}
          onMouseEnter={(e) => e.currentTarget.style.background = '#A0522D'}
          onMouseLeave={(e) => e.currentTarget.style.background = WALNUT}
        >
          Save Appearance Settings
        </button>
      </div>
    </div>
  );
}

function ColorSwatch({ color, label }) {
  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{
        width: '100%',
        height: '50px',
        background: color,
        borderRadius: '8px',
        marginBottom: '6px',
        border: '1px solid rgba(0,0,0,0.1)'
      }} />
      <div style={{
        fontSize: '11px',
        fontWeight: '600',
        color: STONE
      }}>
        {label}
      </div>
    </div>
  );
}
